<template>
  <div class="home">
    <Header :isBook='!isBook' :isArrival='!getStorageInfo.storageDateEnd ? true : false'/>
    <div class="info-wrapper">
      <div class="delivery-info">
        <div class="delivery-info--content">
          <div class="orange-box">Traveller Info</div>
          <h3 class="direction">
            <slot v-if="!getStorageInfo.storageDateEnd">{{ getOrderInfo.from }} to {{ getOrderInfo.to }}</slot>
            <slot v-if="getStorageInfo.storageDateEnd">Storage</slot>
          </h3>
          <div class='container-info'>
            <p>Full Name*</p>
            <input class="input_full-width" type="text" v-model="customerName" v-on:change="info" required><br><br>
            <p>Email*</p>
            <input class="m-top input_full-width" type="email" v-model="customerEmail"
                  v-on:change="info" @keyup="validateEmail(customerEmail)" required><br><br>
            <small v-if="!validEmail">
              Please enter correct email
            </small><br>
            <p>Country Code | Phone number*</p>
            <vue-tel-input 
                :class="['phone-input', 'm-top', 'input_full-width', { 'phone-error': phoneInteracted && customerPhone && !validPhone }]"
                v-model="customerPhone" 
                @input="onPhoneInput" 
                @validate="validPhoneF"
                @blur="onPhoneBlur"
                mode="international"
                :default-country="'DK'"
                :auto-default-country="false"
                :enable-search="true"
                :valid-characters-only="true"
                :disabled-formatting="false"></vue-tel-input>
            <small v-if="phoneInteracted && customerPhone && !validPhone" class="phone-error-message">
              Please enter a valid phone number
            </small>
            <small v-else style="color: black">*The person must be one of the travellers</small><br>
          </div>
          <div class='container-info'>
            <p>Comments</p>
            <textarea class="input_full-width" rows='5' v-model="customerComment" v-on:change="info"></textarea>
          </div><br>
          <small v-if="bookingDateError" class="booking-date-error">{{ bookingDateError }}</small><br v-if="bookingDateError">
          <div class="buttons-container">
            <button
              v-if="(getOrderInfo.from === 'Hotel' || getOrderInfo.from === 'Cruise' || getOrderInfo.from === 'Private Address') && getOrderInfo.to === 'Airport'"
              class="btn-back"
              @click="$route.query.orderOnDock ? goToService('ArrivalInfo', service) : goTo('ArrivalInfo', service)">Back</button>
            <button
              v-else
              class="btn-back"
              @click="
                $route.query.orderOnDock ? goToService('DeliveryInfo', service) :
                getStorageInfo.storageDateEnd ?
                goTo('StorageInfo', service) :
                goTo('DeliveryInfo', service)"
            >Back</button>
            <button
              class="btn-next"
              :disabled="!customerName || !customerEmail || !validEmail || !validPhone"
              @click="onNext()"
            >
              Next
            </button>

            <vue-element-loading
              :active="isLoading"
              spinner="spinner"
              color="#00a9e0"
            />
          </div>
        </div>
      </div>
      <BasketInfo :isArrival='!getStorageInfo.storageDateEnd ? true : false' class="basket-info-hidden" />
    </div>
    <Links link-location="row" />
    <ContactUs />
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import moment from 'moment-timezone'
import Header from '@/components/Header'
import additionalFunctions from '../mixins/additionalFunctions'
import bookingDateValidation from '@/mixins/bookingDateValidation'
import Links from '@/components/TCAndPrivacyPolicyLinks'
import BasketInfo from '@/components/BasketInfo'
import ContactUs from '@/components/ContactUs'
import { encodeStateForQuery } from '@/utils/stateSerializer'

import { mapGetters, mapMutations } from 'vuex'
const { formatBookingDateForApi } = require('@/utils/bookingDate')

export default {
  name: 'TravellerInfo',

  props: ['service'],

  components: {
    Header,
    ContactUs,
    BasketInfo,
    Links,
    VueElementLoading
  },

  computed: {
    ...mapGetters([
      'getOrderInfo',
      'getStorageInfo',
      'getCustomerInfo',
      'getState',
      'getStateData',
      'priceCalculation',
      'getAllBagsCount',
      'getUrlPrices',
      'getBookingContext',
      'getStorageDays'
    ]),
    totalPrice () {
      if (!this.getStorageInfo.numberOfBags && !this.getStorageInfo.numberOfHeavyBags) {
        return Number(this.priceCalculation(true))
      }
      return Number(this.priceCalculation(false))
    },
    shouldSkipPayment () {
      return Number(this.getUrlPrices.skipPayment) === 1 || Number(this.$route.query.skipPayment) === 1
    }
  },

  mixins: [additionalFunctions, bookingDateValidation],

  data () {
    return {
      isLoading: false,
      isBook: true,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      countryCode: '',
      validEmail: true,
      customerComment: '',
      validPhone: false,
      phoneInteracted: false,
      bookingDateError: ''
    }
  },

  created () {
    for (const key in this.getCustomerInfo) {
      if (key in this._data) {
        this[key] = this.getCustomerInfo[key]
      }
    }
    
    // Ensure customerCountryCode is never empty
    if (!this.getCustomerInfo.customerCountryCode) {
      console.log('Setting default country code in created()')
      this.getCustomerInfo.customerCountryCode = '+45'
      this.addFieldsForCustomerInfo({
        customerCountryCode: '+45'
      })
    }
  },

  mounted () {
    this.goToMainPage()
    // Initialize country code if not set
    if (!this.getCustomerInfo.customerCountryCode) {
      this.getCustomerInfo.customerCountryCode = '+45' // Default to Denmark
      // Also update the store immediately
      this.addFieldsForCustomerInfo({
        customerCountryCode: '+45'
      })
    }
  },

  methods: {
    ...mapMutations(['addFieldsForCustomerInfo', 'goToMainPage', 'addFromTo']),
    validPhoneF (phoneObject) {
      this.validPhone = phoneObject.valid || phoneObject.isValid || false
      
      // Store country code from phoneObject
      if (phoneObject.country && phoneObject.country.dialCode) {
        this.getCustomerInfo.customerCountryCode = '+' + phoneObject.country.dialCode
        // Immediately update store
        this.addFieldsForCustomerInfo({
          customerCountryCode: '+' + phoneObject.country.dialCode
        })
      } else if (!this.getCustomerInfo.customerCountryCode) {
        // Fallback to Denmark if no country is detected
        this.getCustomerInfo.customerCountryCode = '+45'
        this.addFieldsForCustomerInfo({
          customerCountryCode: '+45'
        })
      }
    },
    onPhoneInput (formattedNumber, phoneObject) {
      this.phoneInteracted = true
      
      // Store country code from phoneObject
      if (phoneObject.country && phoneObject.country.dialCode) {
        this.getCustomerInfo.customerCountryCode = '+' + phoneObject.country.dialCode
        // Immediately update store
        this.addFieldsForCustomerInfo({
          customerCountryCode: '+' + phoneObject.country.dialCode
        })
      } else if (!this.getCustomerInfo.customerCountryCode) {
        // Fallback to Denmark if no country is detected
        this.getCustomerInfo.customerCountryCode = '+45'
        this.addFieldsForCustomerInfo({
          customerCountryCode: '+45'
        })
      }
      this.info()
    },
    onPhoneBlur () {
      this.phoneInteracted = true
    },
    info () {
      // Ensure customerCountryCode is always set
      if (!this.getCustomerInfo.customerCountryCode) {
        this.getCustomerInfo.customerCountryCode = '+45'
      }
      
      for (const key in this.getCustomerInfo) {
        if (key in this.getCustomerInfo && (this[key] || this[key] === '')) {
          let valueToStore = this[key]
          
          if (key === 'customerPhone' && this.customerPhone && this.getCustomerInfo.customerCountryCode) {
            const countryCode = this.getCustomerInfo.customerCountryCode.replace('+', '')
            const phoneStr = this.customerPhone.toString().replace(/\D/g, '') // Remove non-digits
            
            if (phoneStr.startsWith(countryCode)) {
              valueToStore = phoneStr.substring(countryCode.length)
            } else {
              valueToStore = phoneStr
            }
          }
          
          this.addFieldsForCustomerInfo({
            [key]: valueToStore
          })
        }
      }
      
      // Always explicitly update customerCountryCode in store
      this.addFieldsForCustomerInfo({
        customerCountryCode: this.getCustomerInfo.customerCountryCode
      })
    },
    goTo (name, service) {
      this.addFromTo({
        from: service
      })

      this.$router.push({
        name: name,
        params: {
          service: service,
          services: this.getOrderInfo.services
        }
      })
    },
    async getInfoFromVuex () {
      this.isLoading = true

      let refValue = document.cookie.split(';') || []

      if (refValue.length) {
        refValue = refValue.find(c => c.includes('ref=')) || ''
        if (refValue) {
          refValue = refValue.split('=')[1]
        }
      }

      const dateNow = new Date()
      const objectMomentTimeZone = moment.tz('Europe/Copenhagen')
      const orderDate = objectMomentTimeZone.format('DD.MM.YYYY')
      const orderTime = objectMomentTimeZone.format('HH:mm:ss')
      const totalObj = {}

      totalObj.customerInfo = {
        ...this.getCustomerInfo,
        ref: refValue
      }
      totalObj.additionalInfo = this.getStateData.additionalInfo
      if (!this.getStorageInfo.numberOfBags && !this.getStorageInfo.numberOfHeavyBags) { // not storage
        totalObj.orderInfo = this.getOrderInfo
        totalObj.additionalInfo.priceInfo = this.priceCalculation(true)
        totalObj.additionalInfo.totalBags = this.getAllBagsCount(true)
        totalObj.orderInfo.arrivalDate = formatDate(this.getOrderInfo.arrivalDate)
        totalObj.orderInfo.orderDate = orderDate
        totalObj.orderInfo.orderTime = orderTime
        totalObj.orderInfo.orderChangedDate = Date.parse(dateNow)
        totalObj.orderInfo.paymentId = 'Without payment'
        
        if (this.getUrlPrices.lastTime) {
          totalObj.orderInfo.lastTime = this.getUrlPrices.lastTime
        }
        
        if (console) console.log('****test = ')
        totalObj.orderInfo.noprintself = false
        totalObj.orderInfo.isOpen = true
      } else { // storage
        totalObj.storageInfo = this.getStorageInfo
        totalObj.additionalInfo.priceInfo = this.priceCalculation(false)
        totalObj.additionalInfo.totalBags = this.getAllBagsCount(false)
        totalObj.storageInfo.storageDateFrom = formatDate(this.getStorageInfo.storageDateFrom)
        totalObj.storageInfo.storageDateEnd = formatDate(this.getStorageInfo.storageDateEnd)
        totalObj.additionalInfo.storageDays = this.getStorageDays
        totalObj.storageInfo.orderDate = orderDate
        totalObj.storageInfo.orderTime = orderTime
        totalObj.storageInfo.paymentId = 'Without payment'
      }
      totalObj.additionalInfo.paidInfo = this.shouldSkipPayment ? 'Not paid' : 'Paid'
      totalObj.bookingEligibility = this.getBookingEligibilityPayload()

      function formatDate (date) {
        return formatBookingDateForApi(date)
      }
      try {
        const response = await fetch(`${process.env.VUE_APP_PROD_MODE === 'true'
          ? process.env.VUE_APP_API_SERVER
          : 'http://localhost:3100'}/order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(totalObj)
        })
        let orderResponse = null
        try {
          orderResponse = await response.json()
        } catch (parseErr) {
          // Keep compatibility with old API responses that return plain text
        }

        if (!response.ok) {
          throw new Error(
            (orderResponse && orderResponse.message) ||
            `Failed to save order. Status: ${response.status}`
          )
        }

        const encodedState = encodeStateForQuery(this.getState)
        const query = {}
        if (encodedState) {
          query.state = encodedState
        }
        query.skipPayment = 1
        const orderNumber = orderResponse && (orderResponse.orderNumber || orderResponse.orderId)
          ? (orderResponse.orderNumber || orderResponse.orderId)
          : ''
        if (orderNumber) {
          query.orderNumber = orderNumber
        }
        this.$router.push({
          name: 'AfterPayment',
          query
        })
      } catch (err) {
        this.bookingDateError = err.message || 'Unable to save the order. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    onNext () {
      const bookingDateError = this.getActiveBookingDateValidationError()
      if (bookingDateError) {
        this.bookingDateError = bookingDateError
        return
      }

      this.bookingDateError = ''
      // Debug: log the customer info being sent
      console.log('Customer info before navigation:', this.getCustomerInfo)
      console.log('CustomerCountryCode:', this.getCustomerInfo.customerCountryCode)
      
      if (this.totalPrice < 1 || this.shouldSkipPayment) {
        return this.getInfoFromVuex()
      }
      return this.$route.query.orderOnDock ? this.goToService('OrderDetails', this.service) : this.goTo('OrderDetails', this.service)
    }
  },

  watch: {
    customerEmail: {
      handler: function (val) {
        this.customerEmail = val.trim()
      }
    }
  }
}
</script>

<style>
  .country-selector__input {
    padding-right: 0 !important;
  }
</style>

<style scoped lang="scss">

  input {
    height: 40px;
  }

  .orange-box {
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: 600;
    letter-spacing: 0.1em;
    font-size: 20px;
  }

  p {
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: 600;
    letter-spacing: 0.1em;
  }

  .m-top, .home .input_full-width {
    margin-top: 2px;
  }

  .phone-input {
    border: 1px solid rgb(204, 204, 204);
    border-radius: 10px;
  }

  input[type=radio] {
    width: auto;
    box-shadow: none;
    margin-right: 10px;
    width: 1.5em; height: 1.5em;
  }

  label {
    top: -5px;
    position: relative;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.1rem;
    color: #53565A;
  }

  textarea {
    width: 100%;
    border: 1px solid #53565A;
    border-radius: 10px;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: 500;
    font-size: 20px;
    padding: 15px;
    -webkit-box-shadow: inset 0px 0.5px 1px 0.5px #53565A;
    -moz-box-shadow: inset 0px 0.5px 1px 0.5px #53565A;
    box-shadow: inset 0px 0.5px 1px 0.5px #cccccc;
  }
</style>

<style>
  @media screen and (max-width: 390px) {
    .input-tel__label {
      display: none;
    }
  }

  .country-selector__input, .input-tel__input {
    border-color: #ccc !important;
  }

  /* Phone validation styles */
  .phone-error .vti__input {
    border-color: #e53e3e !important;
    box-shadow: 0 0 0 1px #e53e3e !important;
  }
  
  .phone-error .vti__dropdown {
    border-color: #e53e3e !important;
  }

  .phone-error-message {
    color: #e53e3e;
    font-size: 12px;
    margin-top: 4px;
    display: block;
  }

  /* Success state for valid phone */
  .phone-input:not(.phone-error) .vti__input:focus {
    border-color: #38a169;
    box-shadow: 0 0 0 1px #38a169;
  }
</style>
